//@ts-nocheck
import React, { useEffect, useState, useRef } from "react";
import { Button, DatePicker, Input, Select, message } from "antd";
import {
  CaretLeftOutlined,
  CaretRightOutlined,
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createManufacturer,
  createQuotesResponse,
  createStatus,
  deleteManufacturer,
  deleteQuotesResponse,
  deleteStatus,
  getCompanies,
  getManufacturers,
  getQuotesResponses,
  getQuotesResponsesByQuoteId,
  getQuotesResponsesNextId,
  getStatuses,
  updateManufacturer,
  updateQuotesResponse,
  updateStatus,
  bulkImportQuoteResponses,
  type QuotesResponseItem,
} from "../../utils/API";
import NavigationDropdown from "../../Components/NavigationDropDown";
import PageLogoutButton from "../../Components/PageLogoutButton";
import CommonHeader from "../../Components/commonHeader";
import { normalizeSortRules, sortRulesToQuery, type SortRule } from "../../utils/sortRules";

const RESPONSES_SIMPLE_EXPORT_COLUMNS = [
  { label: "Date", key: "date" },
  { label: "Company", key: "company" },
  { label: "Brand", key: "manu" },
  { label: "Model", key: "model" },
  { label: "Options", key: "options" },
  { label: "QTY", key: "qty" },
  { label: "Price", key: "price" },
  { label: "Asking Price", key: "askingPrice" },
  { label: "Seller Type", key: "dealer" },
  { label: "Have PO", key: "havePo" },
  { label: "Status", key: "statusText" },
  { label: "WTB Type", key: "responseType" },
  { label: "Sub Brand", key: "subBrand" },
  { label: "Sub Model", key: "subModel" },
  { label: "Email", key: "email" },
  { label: "Email Subject", key: "subject" },
  { label: "WTB RE Email", key: "wtbReEmail" },
  { label: "WTBD RE Email", key: "wtbdReEmail" },
  { label: "RSS Note", key: "rssNote" },
  { label: "Comments", key: "comments" },
];

const RESPONSES_SIMPLE_SORT_KEYS = RESPONSES_SIMPLE_EXPORT_COLUMNS.map((column) => column.key);

type ResponseRow = {
  qid?: number;
  localKey: string;
  id: string;
  company: string;
  manu: string;
  model: string;
  options: string;
  officeId: string;
  name: string;
  price: string;
  date: string;
  qty: string;
  responseType: "WTB" | "WTS" | "WTBD" | "WTSQB" | "DWIN" | "WTS-RENT";
  dealer: boolean;
  comments: string;
  email: string;
  subject: string;
  subBrand: string;
  subModel: string;
  askingPrice: string;
  havePo: "Yes" | "No";
  statusText: string;
  companyRe: string;
  wtbReEmail: string;
  wtbdReEmail: string;
  rssNote: string;
};

const DATE_TIME_FORMAT = "MM/DD/YYYY hh:mm A";
const PAGE_SIZE = 1000;

const createBlankRow = (
  model = "",
  quoteId = "",
  initialId = "",
): ResponseRow => ({
  localKey: `simple-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  id: initialId,
  company: "",
  manu: "",
  model,
  options: "",
  officeId: quoteId,
  name: "",
  price: "",
  date: dayjs().format(DATE_TIME_FORMAT),
  qty: "",
  responseType: "WTB",
  dealer: true,
  comments: "",
  email: "",
  subject: "",
  subBrand: "",
  subModel: "",
  askingPrice: "",
  havePo: "No",
  statusText: "",
  companyRe: "",
  wtbReEmail: "",
  wtbdReEmail: "",
  rssNote: "",
});

const mapApiToRow = (item: QuotesResponseItem): ResponseRow => {
  const listType = String(item.listType || "WTB").toUpperCase();
  const responseType: ResponseRow["responseType"] =
    listType === "WTS" ||
    listType === "WTBD" ||
    listType === "WTSQB" ||
    listType === "DWIN" ||
    listType === "WTS-RENT"
      ? (listType as ResponseRow["responseType"])
      : "WTB";
  const stripTz = (v: string) => v.replace(/Z$/, "").replace(/[+-]\d{2}:?\d{2}$/, "");
  const parsed = item.dateQuoted
    ? dayjs(stripTz(String(item.dateQuoted)))
    : item.createdDate
      ? dayjs(stripTz(String(item.createdDate)))
      : null;

  return {
    qid: item.qid,
    localKey: `simple-${item.qid}`,
    id: String(item.id || item.qid || ""),
    company: item.companyName || "",
    manu: item.eqBrand || "",
    model: item.eqModel || "",
    options: item.options || "",
    officeId: String(item.recordId || ""),
    name: item.dealerQuoted || "",
    price: item.price != null ? Number(item.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "",
    date: parsed && parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : "",
    qty: item.qty || "",
    responseType,
    dealer: String(item.sellerType || "").toLowerCase().includes("dealer"),
    comments: item.comment || item.salesComments || "",
    email: item.email || "",
    subject: item.demailSubject || "",
    subBrand: item.subBrand || "",
    subModel: item.sub || "",
    askingPrice: item.priceTarget || "",
    havePo: String(item.havePO || "").toLowerCase() === "yes" ? "Yes" : "No",
    statusText: item.dStatus || "",
    companyRe: item.companyName || "",
    wtbReEmail: item.wtbReplyEmail || "",
    wtbdReEmail: item.wtbdReplyEmail || "",
    rssNote: (item as any).rssNote || "",
  };
};

const parsePriceNumber = (value: string) => {
  const parsed = Number(String(value || "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const mapRowToPayload = (row: ResponseRow): Partial<QuotesResponseItem> => ({
  // Use `?? ""` for text fields so a cleared value is actually sent to the
  // backend (and the cell can be blanked). `|| undefined` would drop the empty
  // string and the backend would keep the old value.
  id: row.id || undefined,
  comment: row.comments ?? "",
  companyName: row.company ?? "",
  createdDate: row.date
    ? dayjs(row.date, DATE_TIME_FORMAT).toISOString()
    : null,
  demailSubject: row.subject ?? "",
  dStatus: row.statusText ?? "",
  email: row.email ?? "",
  havePO: row.havePo ?? "",
  price: row.price ? parsePriceNumber(row.price) : null,
  priceTarget: row.askingPrice ?? "",
  priceString: row.price ?? "",
  qty: row.qty ?? "",
  eqBrand: row.manu ?? "",
  eqModel: row.model ?? "",
  options: row.options ?? "",
  sub: row.subModel ?? "",
  subBrand: row.subBrand ?? "",
  dateQuoted: row.date || null,
  dealerQuoted: row.name ?? "",
  salesComments: row.comments ?? "",
  sellerType: row.dealer ? "Dealer" : "User",
  wtbdReplyEmail: row.wtbdReEmail ?? "",
  wtbReplyEmail: row.wtbReEmail ?? "",
  rssNote: row.rssNote ?? "",
  listType: row.responseType,
  // record_id is an INTEGER column — send null (not "") when no office is set,
  // otherwise Postgres rejects "" with "invalid input syntax for type integer".
  recordId: row.officeId ? Number(row.officeId) : null,
});

const exactMatchFilter = (input: string, option: any) =>
  String(option?.label ?? "").toLowerCase().startsWith(input.toLowerCase());

const fieldCls = "h-6 text-[12px] border border-[#A7A7A7] rounded bg-white";
const btnStyle: React.CSSProperties = { backgroundColor: "#c7c7c7", borderColor: "#000", color: "#000", boxShadow: "1px 1px 3px rgba(0,0,0,0.25)", borderRadius: 0 };
const btnCls = "h-7 !rounded-none !text-[11px]";
const ROW_BACKGROUND = "#edf1f5";
const ROW_BORDER = "#d2d8df";
const STATUS_TYPE_RESPONSE = "response";

type StatusOption = {
  id?: number;
  value: string;
  label: string;
  type?: string;
};

interface Manufacturer {
  id: number;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

const SIMPLE_STATUS_OPTIONS = [
  "SOLD !",
  "Want Target",
  "$$ Change",
  "Offer",
  "No Interest",
  "Make Bid",
  "Offer Made",
  "Call",
  "Working W/Seller",
  "Bid Submitted",
  "Rental Only",
  "Cole Comments",
  "Closed",
  "Sellers Price",
  "We Purchased",
].map((value) => ({ value, label: value }));

const QuotesResponsesSimple: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [rows, setRows] = useState<ResponseRow[]>([]);
  const [subBrandSearch, setSubBrandSearch] = useState("");
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(SIMPLE_STATUS_OPTIONS);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState({ index: -1, value: "" });
  const [newStatus, setNewStatus] = useState("");
  const QRS_STATE_KEY = "quotesResponsesSimpleState";
  const _qrsSaved = (() => { try { return JSON.parse(localStorage.getItem(QRS_STATE_KEY) || "null") || {}; } catch { return {}; } })();
  const [sortRules, setSortRules] = useState<SortRule[]>(() => normalizeSortRules(_qrsSaved.sortRules, RESPONSES_SIMPLE_SORT_KEYS));
  const [currentIndex, setCurrentIndex] = useState(_qrsSaved.currentIndex || 0);
  const qrsInitialRef = useRef(true);
  const navBeltRef = useRef<HTMLDivElement | null>(null);
  const navBeltDragging = useRef(false);
  const [navBeltDragIdx, setNavBeltDragIdx] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextId, setNextId] = useState(1);
  const [manufacturerOptions, setManufacturerOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [isManufacturerModalOpen, setIsManufacturerModalOpen] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState<{ id: number; name: string } | null>(null);
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [companyOptions, setCompanyOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unfilteredTotal, setUnfilteredTotal] = useState(_qrsSaved.unfilteredTotal || 0);
  const buildSortParams = (rules = sortRules) => {
    const sort = sortRulesToQuery(rules, RESPONSES_SIMPLE_SORT_KEYS);
    return sort ? { sort } : {};
  };
  const [isFilterActive, setIsFilterActive] = useState(_qrsSaved.isFilterActive || false);
  const [isFindMode, setIsFindMode] = useState(_qrsSaved.isFindMode || false);
  const emptyFind = { id: "", model: "", manu: "", company: "", options: "", email: "", qty: "", price: "", sellerType: "", dStatus: "", date: "" };
  const [findFields, setFindFields] = useState(_qrsSaved.findFields || emptyFind);
  const findDateRef = useRef("");

  const current = rows[currentIndex] || null;
  const quoteIdParam = String(searchParams.get("quoteId") || "").trim();
  const modelParam = String(searchParams.get("model") || "").trim();

  const withQuoteId = (path: string) => {
    if (!quoteIdParam) return path;
    return `${path}?quoteId=${encodeURIComponent(quoteIdParam)}`;
  };

  const loadManufacturers = async () => {
    try {
      const data = await getManufacturers();
      const list = Array.isArray(data) ? data : [];
      setManufacturers(list);
      const options = list.map((m: any) => ({
        value: m?.name,
        label: m?.name,
      }));
      setManufacturerOptions(options);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load manufacturers");
    }
  };

  const ensureOneBlank = async () => {
    const newId = await getQuotesResponsesNextId();
    const blank = createBlankRow(modelParam, quoteIdParam, String(newId));
    setRows([blank]);
    setCurrentIndex(0);
    setCurrentPage(1);
    setTotalPages(1);
    setTotalRecords(1);
    setNextId(newId);
  };

  const loadNextAvailableId = async () => {
    try {
      const value = await getQuotesResponsesNextId();
      setNextId(value);
      return value;
    } catch (error) {
      console.error("Failed to get next ID:", error);
      return 1;
    }
  };

  const loadPage = async (page: number, preferredIndex = 0, rulesOverride = sortRules) => {
    setLoadingPage(true);
    try {
      const res = await getQuotesResponses({ page, limit: PAGE_SIZE, ...buildSortParams(rulesOverride) });
      const list = Array.isArray(res?.data) ? res.data : [];

      if (!list.length) {
        await ensureOneBlank();
        return;
      }

      setRows(list.map(mapApiToRow));
      const total = Number(res?.pagination?.total || list.length);
      const pages = Math.max(1, Number(res?.pagination?.totalPages || 1));
      setTotalRecords(total);
      setUnfilteredTotal(total);
      setIsFilterActive(false);
      setTotalPages(pages);
      setCurrentPage(Math.min(Math.max(1, page), pages));
      const safeIndex = Math.min(Math.max(0, preferredIndex), list.length - 1);
      setCurrentIndex(safeIndex);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load quote responses");
      setRows([]);
      setCurrentIndex(0);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setIsFilterActive(false);
      setUnfilteredTotal(0);
    } finally {
      setLoadingPage(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await getCompanies({ page: 1, limit: 1000 });
      const list = Array.isArray(response?.data) ? response.data : [];
      const seen = new Set<string>();
      const options = list
        .map((company: any) => String(company?.name || "").trim())
        .filter((name: string) => {
          if (!name) return false;
          const key = name.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((name: string) => ({ value: name, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setCompanyOptions(options);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load companies");
    }
  };

  const loadStatuses = async () => {
    try {
      const data = await getStatuses();
      if (Array.isArray(data) && data.length > 0) {
        const mapped = data
          .filter((item: any) => item.type === STATUS_TYPE_RESPONSE)
          .map((item: any) => ({
            id: item.id,
            value: item.statusName,
            label: item.statusName,
            type: item.type,
          }));
        setStatusOptions(mapped.length > 0 ? mapped : SIMPLE_STATUS_OPTIONS);
      } else {
        setStatusOptions(SIMPLE_STATUS_OPTIONS);
      }
    } catch (error) {
      console.error("Failed to load statuses", error);
    }
  };

  const handleEditStatus = (index: number) => {
    setEditingStatus({ index, value: statusOptions[index]?.label || "" });
  };

  const handleSaveStatus = async () => {
    if (editingStatus.index < 0 || !editingStatus.value.trim()) return;
    const targetStatus = statusOptions[editingStatus.index];
    if (!targetStatus?.id) return;

    try {
      await updateStatus(targetStatus.id, {
        statusName: editingStatus.value.trim(),
        type: targetStatus.type || STATUS_TYPE_RESPONSE,
      });
      setEditingStatus({ index: -1, value: "" });
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to update status");
    }
  };

  const handleAddStatus = async () => {
    if (!newStatus.trim()) return;

    try {
      await createStatus({
        statusName: newStatus.trim(),
        type: STATUS_TYPE_RESPONSE,
      });
      setNewStatus("");
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add status");
    }
  };

  const handleDeleteStatus = async (index: number) => {
    const targetStatus = statusOptions[index];
    if (!targetStatus?.id) return;

    try {
      await deleteStatus(targetStatus.id);
      await loadStatuses();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete status");
    }
  };

  const handleAddManufacturer = async () => {
    if (!newManufacturerName.trim()) { message.warning("Please enter a manufacturer name"); return; }
    try {
      await createManufacturer(newManufacturerName.trim());
      setNewManufacturerName("");
      await loadManufacturers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to add manufacturer");
    }
  };

  const handleUpdateManufacturer = async () => {
    if (!editingManufacturer || !editingManufacturer.name.trim()) { message.warning("Please enter a manufacturer name"); return; }
    try {
      await updateManufacturer(editingManufacturer.id, editingManufacturer.name.trim());
      setEditingManufacturer(null);
      await loadManufacturers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to update manufacturer");
    }
  };

  const handleDeleteManufacturer = async (id: number) => {
    try {
      await deleteManufacturer(id);
      await loadManufacturers();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to delete manufacturer");
    }
  };

  const getGlobalRecordIndex = () => {
    if (rows.length === 0) return 0;
    return (Math.max(currentPage, 1) - 1) * PAGE_SIZE + currentIndex;
  };

  const jumpToGlobalRecord = async (globalIndex: number) => {
    const safeIndex = Math.min(Math.max(0, globalIndex), Math.max(totalRecords - 1, 0));

    if (totalPages === 1) {
      setCurrentIndex(Math.min(safeIndex, Math.max(rows.length - 1, 0)));
      return;
    }

    const targetPage = Math.floor(safeIndex / PAGE_SIZE) + 1;
    const targetIndex = safeIndex % PAGE_SIZE;

    if (targetPage === currentPage) {
      setCurrentIndex(Math.min(targetIndex, Math.max(rows.length - 1, 0)));
      return;
    }

    await loadPage(targetPage, targetIndex);
  };

  const loadByQuoteId = async () => {
    try {
      const list = await getQuotesResponsesByQuoteId(quoteIdParam);
      if (!Array.isArray(list) || list.length === 0) {
        await ensureOneBlank();
        return;
      }
      setRows(list.map(mapApiToRow));
      setCurrentIndex(0);
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(list.length);
      setIsFilterActive(false);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load quote responses");
    }
  };

  useEffect(() => {
    // Don't save during initial load — currentIndex is temporarily 0 before data arrives
    if (!qrsInitialRef.current) {
      const currentQid = rows[currentIndex]?.qid ?? null;
      localStorage.setItem(QRS_STATE_KEY, JSON.stringify({ currentIndex, currentPage, currentQid, isFilterActive, isFindMode, findFields, unfilteredTotal, sortRules }));
    }
  }, [currentIndex, currentPage, isFilterActive, isFindMode, findFields, unfilteredTotal, sortRules]);

  useEffect(() => {
    const initialize = async () => {
      setLoadingPage(true);
      try {
        await loadManufacturers();
        await loadCompanies();
        await loadStatuses();
        await loadNextAvailableId();

        // Re-apply saved find filter if active
        const savedFind = _qrsSaved.findFields || {};
        const hasSavedFind = _qrsSaved.isFilterActive && Object.values(savedFind).some((v) => String(v || "").trim());
        if (hasSavedFind) {
          const params: any = { page: 1, ...buildSortParams() };
          if (savedFind.id?.trim())         params.id         = savedFind.id.trim();
          if (savedFind.model?.trim())      params.model      = savedFind.model.trim();
          if (savedFind.manu?.trim())       params.manu       = savedFind.manu.trim();
          if (savedFind.company?.trim())    params.company    = savedFind.company.trim();
          if (savedFind.options?.trim())    params.options    = savedFind.options.trim();
          if (savedFind.email?.trim())      params.email      = savedFind.email.trim();
          if (savedFind.qty?.trim())        params.qty        = savedFind.qty.trim();
          if (savedFind.price?.trim())      params.price      = savedFind.price.trim();
          if (savedFind.sellerType?.trim()) params.sellerType = savedFind.sellerType.trim();
          if (savedFind.dStatus?.trim())    params.dStatus    = savedFind.dStatus.trim();
          if (savedFind.date?.trim())       params.date       = savedFind.date.trim();
          const fres = await getQuotesResponses(params);
          const flist = Array.isArray(fres?.data) ? fres.data : [];
          if (flist.length > 0) {
            const fmapped = flist.map(mapApiToRow);
            setRows(fmapped);
            setTotalRecords(flist.length);
            setIsFilterActive(true);
            setTotalPages(1);
            setCurrentPage(1);
            qrsInitialRef.current = false;
            const savedQid = _qrsSaved.currentQid;
            if (savedQid != null) {
              const qidIdx = fmapped.findIndex((r) => r.qid === savedQid || String(r.qid) === String(savedQid));
              setCurrentIndex(qidIdx >= 0 ? qidIdx : Math.min(_qrsSaved.currentIndex || 0, fmapped.length - 1));
            } else {
              setCurrentIndex(Math.min(_qrsSaved.currentIndex || 0, fmapped.length - 1));
            }
          } else {
            await loadInitialRows();
          }
        } else {
          await loadInitialRows();
        }

        async function loadInitialRows() {
          const savedPage = _qrsSaved.currentPage || 1;
          const res = await getQuotesResponses({ page: savedPage, limit: PAGE_SIZE, ...buildSortParams() });
          const list = Array.isArray(res?.data) ? res.data : [];
          if (!list.length) {
            // Saved page may be out of range — fall back to page 1
            const fallback = await getQuotesResponses({ page: 1, limit: PAGE_SIZE, ...buildSortParams() });
            const flist = Array.isArray(fallback?.data) ? fallback.data : [];
            if (!flist.length) { await ensureOneBlank(); return; }
            const mapped = flist.map(mapApiToRow);
            setRows(mapped);
            setTotalRecords(Number(fallback?.pagination?.total || flist.length));
            setTotalPages(Math.max(1, Number(fallback?.pagination?.totalPages || 1)));
            setCurrentPage(1);
            qrsInitialRef.current = false;
            setCurrentIndex(0);
          } else {
            const mapped = list.map(mapApiToRow);
            setRows(mapped);
            const total = Number(res?.pagination?.total || list.length);
            const pages = Math.max(1, Number(res?.pagination?.totalPages || 1));
            setTotalRecords(total);
            setTotalPages(pages);
            setCurrentPage(savedPage);
            qrsInitialRef.current = false;
            // Restore by qid first (exact record match), fall back to saved within-page index
            const savedQid = _qrsSaved.currentQid;
            if (savedQid != null) {
              const qidIdx = mapped.findIndex((r) => r.qid === savedQid || String(r.qid) === String(savedQid));
              setCurrentIndex(qidIdx >= 0 ? qidIdx : Math.min(_qrsSaved.currentIndex || 0, mapped.length - 1));
            } else {
              setCurrentIndex(_qrsSaved.currentIndex > 0 ? Math.min(_qrsSaved.currentIndex, mapped.length - 1) : 0);
            }
          }
        }
      } catch (error: any) {
        message.error(error?.response?.data?.message || "Failed to initialize page");
        setRows([]);
      } finally {
        setLoadingPage(false);
      }
    };
    initialize();
  }, [quoteIdParam, modelParam]);

  const updateCurrent = (patch: Partial<ResponseRow>) => {
    if (!current) return;
    setRows((prev) =>
      prev.map((item, idx) => (idx === currentIndex ? { ...item, ...patch } : item)),
    );
  };

  const handleNew = async () => {
    try {
      const newId = nextId || (await loadNextAvailableId());
      const blank = createBlankRow(modelParam, quoteIdParam, String(newId));
      setRows((prev) => [blank, ...prev]);
      setCurrentIndex(0);

      const payload = mapRowToPayload(blank);
      if (!payload.listType) payload.listType = "WTB";

      const saved = await createQuotesResponse(payload);
      setRows((prev) =>
        prev.map((row) =>
          row.localKey === blank.localKey
            ? {
                ...row,
                qid: saved.qid,
                id: String(saved.id || newId),
              }
            : row,
        ),
      );

      await loadNextAvailableId();
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to create new row");
    }
  };

  // Duplicate the currently open record: copy every field, but give it the
  // latest ID and the current timestamp (and a fresh row identity / no qid so
  // it saves as a brand-new record).
  const handleDuplicate = async () => {
    if (!current) {
      message.warning("No record to duplicate");
      return;
    }
    try {
      const newId = nextId || (await loadNextAvailableId());
      const duplicated: ResponseRow = {
        ...current,
        localKey: `simple-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        qid: undefined,
        id: String(newId),
        date: dayjs().format(DATE_TIME_FORMAT),
      };

      const payload = mapRowToPayload(duplicated);
      if (!payload.listType) payload.listType = "WTB";

      const saved = await createQuotesResponse(payload);
      const newQid = saved?.qid;
      await loadNextAvailableId();

      // Helper: select the new record by qid, else fall back to the last row.
      const pickIndex = (mapped: ResponseRow[]) => {
        const idx = mapped.findIndex(
          (r) => newQid != null && String(r.qid) === String(newQid),
        );
        return idx >= 0 ? idx : Math.max(0, mapped.length - 1);
      };

      // Always land on the new duplicate in the GLOBAL list at its last
      // position — even when opened via ?quoteId=. The duplicate gets a fresh
      // global id and is no longer tied to a single quote, so the by-quote list
      // wouldn't contain it. Use the SERVER's real totals (no faked counts) so
      // navigation stays consistent. Records are oldest-first (no-date rows are
      // sorted first), so the newest one is on the last page: read page 1 to
      // learn the page count, then load that page.
      const probe = await getQuotesResponses({ page: 1, limit: PAGE_SIZE, ...buildSortParams() });
      const total = Number(probe?.pagination?.total || 0);
      const pages = Math.max(1, Number(probe?.pagination?.totalPages || 1));
      const res =
        pages === 1
          ? probe
          : await getQuotesResponses({ page: pages, limit: PAGE_SIZE, ...buildSortParams() });
      const mapped = (Array.isArray(res?.data) ? res.data : []).map(mapApiToRow);
      setRows(mapped);
      setTotalRecords(total || mapped.length);
      setUnfilteredTotal(total || mapped.length);
      setIsFilterActive(false);
      setCurrentPage(pages);
      setTotalPages(pages);
      setCurrentIndex(pickIndex(mapped));
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to duplicate record");
    }
  };

  const handleSave = async () => {
    if (!current) return;
    try {
      setSaving(true);
      const payload = mapRowToPayload(current);
      // Recover qid from localKey (e.g. "simple-93945407") if state lost it
      const recoveredQid = current.qid
        || (current.localKey ? Number(current.localKey.replace("simple-", "")) || null : null);
      if (recoveredQid) {
        const { id: _id, ...updatePayload } = payload;
        await updateQuotesResponse(recoveredQid, updatePayload);
        if (!current.qid) updateCurrent({ qid: recoveredQid });
      } else {
        const created = await createQuotesResponse(payload);
        updateCurrent({ qid: created.qid, id: String(created.id || created.qid || "") });
      }
      // message.success("Saved successfully");
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDateChangeAndSave = async (dateString: string) => {
    if (!current) return;
    updateCurrent({ date: dateString });
    const qid = current.qid
      || (current.localKey ? Number(current.localKey.replace("simple-", "")) || null : null);
    if (!qid) return;
    const updatedRow = { ...current, date: dateString };
    const { id: _id, ...payload } = mapRowToPayload(updatedRow);
    try {
      await updateQuotesResponse(qid, payload);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to save date");
    }
  };

  const handleDeleteCurrentRecord = async () => {
    const currentRow = rows.length > 0 ? rows[Math.min(currentIndex, rows.length - 1)] : null;
    if (!currentRow) {
      message.warning("No response record selected to delete");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to delete this quote response record?",
    );
    if (!confirmed) return;

    const globalIndexBeforeDelete = getGlobalRecordIndex();
    // After deletion the list shrinks by one. Keeping the same global index
    // lands on the record that followed the deleted one (the "next"); if we
    // deleted the last record there is no next, so clamp down to the new last
    // record (the "previous").
    const newTotal = Math.max(0, totalRecords - 1);
    const targetGlobalIndex = Math.max(
      0,
      Math.min(globalIndexBeforeDelete, newTotal - 1),
    );

    try {
      if (currentRow.qid) {
        await deleteQuotesResponse(currentRow.qid);
      } else {
        // Unsaved row: drop it locally and stay on the same index (next record
        // shifts into this slot), falling back to the last remaining row.
        setRows((prev) => {
          const filtered = prev.filter((row) => row.localKey !== currentRow.localKey);
          setCurrentIndex(Math.max(0, Math.min(currentIndex, filtered.length - 1)));
          return filtered;
        });
        return;
      }

      if (quoteIdParam) {
        const list = await getQuotesResponsesByQuoteId(quoteIdParam);
        if (!Array.isArray(list) || list.length === 0) {
          await ensureOneBlank();
          return;
        }
        const mapped = list.map(mapApiToRow);
        setRows(mapped);
        setTotalRecords(mapped.length);
        setCurrentPage(1);
        setTotalPages(1);
        setCurrentIndex(Math.max(0, Math.min(currentIndex, mapped.length - 1)));
      } else {
        const targetPage = Math.floor(targetGlobalIndex / PAGE_SIZE) + 1;
        const targetIndex = targetGlobalIndex % PAGE_SIZE;
        await loadPage(targetPage, targetIndex);
      }
    } catch (error: any) {
      message.error(
        error?.response?.data?.message ||
          "Failed to delete quote response record",
      );
    }
  };

  const currentRecordDisplay = rows.length > 0 ? getGlobalRecordIndex() + 1 : 0;
  const [recordInputValue, setRecordInputValue] = useState("");

  useEffect(() => {
    setRecordInputValue(rows.length > 0 ? String(currentRecordDisplay) : "");
  }, [currentRecordDisplay, rows.length]);

  const handleApplySortRules = async (rules: SortRule[]) => {
    const normalized = normalizeSortRules(rules, RESPONSES_SIMPLE_SORT_KEYS);
    setSortRules(normalized);
    setIsFilterActive(false);
    setCurrentIndex(0);
    await loadPage(1, 0, normalized);
  };

  const handleRecordInputCommit = async (value: string) => {
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed) || parsed < 1 || parsed > totalRecords) {
      setRecordInputValue(rows.length > 0 ? String(currentRecordDisplay) : "");
      return;
    }
    await jumpToGlobalRecord(parsed - 1);
  };

  const handleShowAll = async () => {
    try {
      setLoadingPage(true);
      const res = await getQuotesResponses({ page: 1, limit: PAGE_SIZE });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list.map(mapApiToRow));
      setTotalRecords(Number(res?.pagination?.total || list.length));
      setUnfilteredTotal(Number(res?.pagination?.total || list.length));
      setTotalPages(Math.max(1, Number(res?.pagination?.totalPages || 1)));
      setCurrentPage(1);
      setCurrentIndex(0);
      setIsFilterActive(false);
      setIsFindMode(false);
      setSortRules([]);
      setFindFields(emptyFind);
      findDateRef.current = "";
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to load records");
    } finally {
      setLoadingPage(false);
    }
  };

  const openFindMode = () => {
    findDateRef.current = "";
    setFindFields(emptyFind);
    setIsFindMode(true);
  };

  const cancelFindMode = () => {
    findDateRef.current = "";
    setIsFindMode(false);
    setFindFields(emptyFind);
  };

  const handlePerformFind = async () => {
    const f = findFields;
    const dateVal = findDateRef.current.trim();
    const hasAny = Object.values(f).some((v) => String(v || "").trim()) || !!dateVal;
    if (!hasAny) { message.warning("Enter at least one search value"); return; }
    // capture unfiltered total if not already known
    if (unfilteredTotal === 0) {
      try {
        const meta = await getQuotesResponses({ page: 1, limit: 1 });
        setUnfilteredTotal(Number(meta?.pagination?.total || 0));
      } catch { /* ignore */ }
    }
    const params: any = { page: 1, ...buildSortParams() };
    if (f.id.trim())         params.id         = f.id.trim();
    if (f.model.trim())      params.model      = f.model.trim();
    if (f.manu.trim())       params.manu       = f.manu.trim();
    if (f.company.trim())    params.company    = f.company.trim();
    if (f.options.trim())    params.options    = f.options.trim();
    if (f.email.trim())      params.email      = f.email.trim();
    if (f.qty.trim())        params.qty        = f.qty.trim();
    if (f.price.trim())      params.price      = f.price.trim();
    if (f.sellerType.trim()) params.sellerType = f.sellerType.trim();
    if (f.dStatus.trim())    params.dStatus    = f.dStatus.trim();
    if (dateVal)             params.date       = dateVal;
    try {
      const res = await getQuotesResponses(params);
      const list = Array.isArray(res?.data) ? res.data : [];
      if (!list.length) { message.info("No records found"); return; }
      const filteredTotal = Number(res?.pagination?.total || list.length);
      setRows(list.map(mapApiToRow));
      setTotalRecords(filteredTotal);
      setIsFilterActive(true);
      setTotalPages(Math.max(1, Number(res?.pagination?.totalPages || 1)));
      setCurrentPage(1);
      setCurrentIndex(0);
    } catch { message.error("Search failed"); }
    setIsFindMode(false);
  };

  const fi = (field: string, className = "", style: React.CSSProperties = {}) => (
    <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
      <Input
        className={className}
        style={style}
        value={(findFields as any)[field] || ""}
        onChange={(e) => setFindFields((p) => ({ ...p, [field]: e.target.value }))}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handlePerformFind(); } }}
      />
      <SearchOutlined style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: 11, pointerEvents: "none" }} />
    </div>
  );

  const handleRss = () => {
    const model = current?.model?.trim();
    const note = dayjs().format("MM/DD/YYYY HH:mm");
    updateCurrent({ rssNote: note });
    const dest = model ? `/Quotes?model=${encodeURIComponent(model)}` : "/Quotes";
    navigate(dest);
  };

  const handleGoToRecord = async (n: number) => {
    if (!Number.isFinite(n) || n < 1 || n > totalRecords) return;
    await jumpToGlobalRecord(n - 1);
  };

  const navigateToPrevious = async () => {
    if (loadingPage) return;
    const globalIndex = getGlobalRecordIndex();
    if (globalIndex > 0) {
      await jumpToGlobalRecord(globalIndex - 1);
      return;
    }
    message.warning("No previous record");
  };

  const navigateToNext = async () => {
    if (loadingPage) return;
    const globalIndex = getGlobalRecordIndex();
    if (globalIndex < totalRecords - 1) {
      await jumpToGlobalRecord(globalIndex + 1);
      return;
    }
    message.warning("No next record");
  };

  const navigateToPreviousPage = async () => {
    if (loadingPage) return;
    if (currentPage > 1) await loadPage(currentPage - 1, 0);
    else message.warning("Already at first page");
  };

  const navigateToNextPage = async () => {
    if (loadingPage) return;
    if (currentPage < totalPages) await loadPage(currentPage + 1, 0);
    else message.warning("Already at last page");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <style>{`
        .date-ok-gray .ant-btn-primary {
          background-color: #b0b0b0 !important;
          border-color: #999 !important;
          color: #000 !important;
          box-shadow: none !important;
        }
        .date-ok-gray .ant-btn-primary:hover,
        .date-ok-gray .ant-btn-primary:focus {
          background-color: #b0b0b0 !important;
          border-color: #999 !important;
          color: #000 !important;
        }
      `}</style>
      <CommonHeader
        className="mb-0"
        currentRecord={recordInputValue}
        disableNext={
          rows.length === 0 || getGlobalRecordIndex() >= totalRecords - 1
        }
        disablePrevious={rows.length === 0 || getGlobalRecordIndex() <= 0}
        isFindMode={isFindMode}
        onCancelFind={cancelFindMode}
        onDeleteRecord={handleDeleteCurrentRecord}
        onFind={openFindMode}
        onNewRecord={handleNew}
        onNextRecord={navigateToNext}
        onPerformFind={handlePerformFind}
        onPreviousRecord={navigateToPrevious}
        onRecordChange={setRecordInputValue}
        onRecordCommit={handleRecordInputCommit}
        onRecordSliderCommit={jumpToGlobalRecord}
        onShowAll={handleShowAll}
        recordCount={totalRecords}
        recordIndex={
          rows.length > 0
            ? Math.min(getGlobalRecordIndex(), Math.max(totalRecords - 1, 0))
            : 0
        }
        rightContent={<PageLogoutButton />}
        title="Quotes Responses Simplified"
        totalActiveCount={totalRecords}
        totalLabel={isFilterActive ? "Filtered" : (sortRules.length > 0 ? "Total (Sorted)" : "Total (Unsorted)")}
        totalRecordCount={isFilterActive ? unfilteredTotal : totalRecords}
        totalValue={isFilterActive ? `${totalRecords} / ${unfilteredTotal}` : totalRecords}
        withQuoteId={withQuoteId}
        sortColumns={RESPONSES_SIMPLE_EXPORT_COLUMNS}
        sortTitle="Sort Quotes Responses Simplified Records"
        sortRules={sortRules}
        onApplySort={handleApplySortRules}
        exportColumns={RESPONSES_SIMPLE_EXPORT_COLUMNS}
        getData={() => rows}
        onImport={async (data) => { await bulkImportQuoteResponses(data); }}
        exportFilename="quote-responses-simple"
      />
      <div
        className="flex justify-start items-center px-2 py-2 border-b border-gray-300"
        style={{ backgroundColor: "#3A6C9E" }}
      >
        <div className="flex items-center gap-3">
          <h1 className="text-base sm:text-xl font-normal text-white">Quotes Responses Simplified</h1>
          <Button
            size="small"
            className="h-[24px] min-w-[70px] !rounded-none !border !border-black !bg-[#c7c7c7] !text-black hover:!bg-[#c7c7c7] hover:!text-black focus:!bg-[#c7c7c7] focus:!text-black px-4 !text-[12px]"
            onClick={handleNew}
          >
            New
          </Button>
          <Button
            size="small"
            disabled={!current}
            className="h-[24px] min-w-[70px] !rounded-none !border !border-black !bg-[#c7c7c7] !text-black hover:!bg-[#c7c7c7] hover:!text-black focus:!bg-[#c7c7c7] focus:!text-black px-4 !text-[12px]"
            onClick={handleDuplicate}
          >
            Duplicate
          </Button>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border p-6" style={{ backgroundColor: ROW_BACKGROUND, borderColor: isFindMode ? "#faad14" : ROW_BORDER, borderWidth: isFindMode ? 3 : 1 }}>
        {loadingPage && <div className="text-sm text-gray-600 mb-3">Loading record...</div>}
        {!current ? null : (
          <div style={{ maxWidth: 500 }}>
          <>
            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-1 text-right text-[12px] font-semibold">ID</div>
              {isFindMode ? (
                <div className="col-span-3">{fi("id", fieldCls)}</div>
              ) : (
                <Input className={`col-span-3 ${fieldCls}`} value={current.id} onChange={(e) => updateCurrent({ id: e.target.value })} />
              )}
              <div className="col-span-2 text-right text-[12px] font-semibold">Date</div>
              <div
                className="col-span-4"
                onPasteCapture={(e) => {
                  if (!isFindMode) return;
                  const pasted = e.clipboardData.getData("text").trim();
                  if (!pasted) return;
                  e.preventDefault();
                  e.stopPropagation();
                  findDateRef.current = pasted;
                  setFindFields((p) => ({ ...p, date: pasted }));
                }}
              >
                <DatePicker
                  className="h-7 rounded w-full"
                  popupClassName="date-ok-gray"
                  showTime={
                    isFindMode
                      ? (findFields.date && /\d{1,2}:\d{2}/.test(findFields.date)
                          ? { format: "hh:mm A" }
                          : false)
                      : { format: "hh:mm A" }
                  }
                  format={
                    isFindMode
                      ? (findFields.date && /\d{1,2}:\d{2}/.test(findFields.date)
                          ? DATE_TIME_FORMAT
                          : "MM/DD/YYYY")
                      : DATE_TIME_FORMAT
                  }
                  value={
                    isFindMode
                      ? (findFields.date ? dayjs(findFields.date) : null)
                      : (current.date ? dayjs(current.date, DATE_TIME_FORMAT) : null)
                  }
                  onChange={(_, dateString) => {
                    if (isFindMode) {
                      const d = String(dateString || "");
                      findDateRef.current = d;
                      setFindFields((p) => ({ ...p, date: d }));
                    } else {
                      handleDateChangeAndSave(String(dateString || ""));
                    }
                  }}
                  onBlur={(e) => {
                    if (isFindMode) return;
                    const typed = (e.target as HTMLInputElement).value;
                    if (typed) handleDateChangeAndSave(typed);
                  }}
                />
              </div>
              {!isFindMode && (
                <div className="col-span-2 flex items-center gap-1">
                  <Button
                    className={`flex-1 !px-1 ${btnCls}`}
                    style={btnStyle}
                    onClick={() => updateCurrent({ date: dayjs().format(DATE_TIME_FORMAT) })}
                  >
                    Now
                  </Button>
                  <Button
                    className={`flex-1 !px-1 ${btnCls}`}
                    style={btnStyle}
                    onClick={handleRss}
                  >
                    RSS
                  </Button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">Company</div>
              {isFindMode ? (
                <Select showSearch allowClear className="col-span-7" value={findFields.company || undefined} options={companyOptions} onChange={(value) => setFindFields((p) => ({ ...p, company: value || "" }))} filterOption={exactMatchFilter} placeholder="Search company..." />
              ) : (
                <Select
                  showSearch
                  className="col-span-7"
                  value={current.company || undefined}
                  options={companyOptions}
                  onChange={(value) => updateCurrent({ company: String(value || "") })}
                  filterOption={exactMatchFilter}
                  placeholder="Select company"
                />
              )}
              {!isFindMode && (
                <Button
                  className={`col-span-3 ${btnCls}`}
                  style={btnStyle}
                  onClick={async () => {
                    try {
                      const newId = nextId || (await loadNextAvailableId());
                      const blank = createBlankRow(modelParam, quoteIdParam, String(newId));
                      const filled = { ...blank, company: current.company, manu: current.manu, model: current.model };
                      setRows((prev) => [filled, ...prev]);
                      setCurrentIndex(0);
                      const payload = mapRowToPayload(filled);
                      if (!payload.listType) payload.listType = "WTB";
                      const saved = await createQuotesResponse(payload);
                      setRows((prev) => prev.map((row) => row.localKey === filled.localKey ? { ...row, qid: saved.qid, id: String(saved.id || newId) } : row));
                      await loadNextAvailableId();
                    } catch (error: any) {
                      message.error(error?.response?.data?.message || "Failed to create new record");
                    }
                  }}
                >
                  New From Here
                </Button>
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">Brand</div>
              {isFindMode ? (
                <Select showSearch allowClear className="col-span-10 font-bold" value={findFields.manu || undefined} options={manufacturerOptions} onChange={(value) => setFindFields((p) => ({ ...p, manu: value || "" }))} filterOption={exactMatchFilter} placeholder="Search brand..." />
              ) : (
                <Select
                  showSearch
                  className="col-span-10 font-bold"
                  value={current.manu || undefined}
                  options={[
                    ...manufacturerOptions,
                    { value: "separator", label: "──────────", disabled: true },
                    { value: "__edit_manufacturer__", label: "✎ Edit Manufacturers..." },
                  ]}
                  onChange={(value) => {
                    if (value === "__edit_manufacturer__") { setIsManufacturerModalOpen(true); return; }
                    updateCurrent({ manu: String(value || "") });
                  }}
                  filterOption={(input, option) =>
                    option?.value === "__edit_manufacturer__" ||
                    option?.disabled === true ||
                    String(option?.label ?? "").toLowerCase().startsWith(input.toLowerCase())
                  }
                />
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">Model</div>
              {isFindMode ? <div className="col-span-10 font-bold">{fi("model", `font-bold ${fieldCls}`)}</div> : (
                <Input className={`col-span-10 font-bold ${fieldCls}`} value={current.model} onChange={(e) => updateCurrent({ model: e.target.value })} />
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">Options</div>
              {isFindMode ? <div className="col-span-10 font-bold">{fi("options", `font-bold ${fieldCls}`)}</div> : (
                <Input className={`col-span-10 font-bold ${fieldCls}`} value={current.options} onChange={(e) => updateCurrent({ options: e.target.value })} />
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">Sub Brand</div>
              <Select
                showSearch
                allowClear
                className="col-span-3"
                value={current.subBrand || undefined}
                searchValue={subBrandSearch}
                onSearch={setSubBrandSearch}
                onDropdownVisibleChange={(open) => { setSubBrandSearch(open ? (current.subBrand || "") : ""); }}
                options={manufacturerOptions}
                onChange={(value) => { updateCurrent({ subBrand: String(value || "") }); setSubBrandSearch(""); }}
                filterOption={exactMatchFilter}
              />
              <div className="col-span-2 text-right text-[12px] font-semibold">Sub Model</div>
              <Input
                className={`col-span-3 ${fieldCls}`}
                value={current.subModel}
                onChange={(e) => updateCurrent({ subModel: e.target.value })}
              />
              <div className="col-span-2 text-left text-[12px] font-semibold">QTY</div>
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1" style={{ marginTop: -4 }}>
              <div className="col-span-2 text-right text-[12px] font-semibold">Price</div>
              {isFindMode ? <div className="col-span-3">{fi("price", fieldCls)}</div> : (
                <Input
                  className={`col-span-3 ${fieldCls}`}
                  prefix="$"
                  value={current.price}
                  onChange={(e) => updateCurrent({ price: e.target.value })}
                  onBlur={(e) => {
                    const n = Number(e.target.value.replace(/[^0-9.-]/g, ""));
                    if (!isNaN(n) && e.target.value.trim()) {
                      updateCurrent({ price: n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) });
                    }
                  }}
                />
              )}
              <div className="col-span-2 text-right text-[12px] font-semibold">Asking Price</div>
              <Input
                className={`col-span-3 ${fieldCls}`}
                value={current.askingPrice}
                onChange={(e) => updateCurrent({ askingPrice: e.target.value })}
              />
              {isFindMode ? <div className="col-span-2">{fi("qty", fieldCls)}</div> : (
                <Input className={`col-span-2 ${fieldCls}`} value={current.qty} onChange={(e) => updateCurrent({ qty: e.target.value })} />
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">SellerType</div>
              {isFindMode ? (
                <Select allowClear className="col-span-4" value={findFields.sellerType || undefined} options={[{ value: "Dealer", label: "Dealer" }, { value: "EU", label: "EU" }]} onChange={(value) => setFindFields((p) => ({ ...p, sellerType: value || "" }))} placeholder="Search..." />
              ) : (
                <div className="col-span-4 flex items-center gap-3 text-[12px]">
                  <label className="flex items-center gap-1"><input type="radio" checked={current.dealer} onChange={() => updateCurrent({ dealer: true })} />Dealer</label>
                  <label className="flex items-center gap-1"><input type="radio" checked={!current.dealer} onChange={() => updateCurrent({ dealer: false })} />EU</label>
                </div>
              )}
              <div className="col-span-2 text-right text-[12px] font-semibold">HavePO:</div>
              <div className="col-span-4 flex items-center gap-4 text-[12px]">
                <label className="flex items-center gap-1">
                  <input type="radio" checked={current.havePo === "No"} onChange={() => updateCurrent({ havePo: "No" })} />
                  No
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" checked={current.havePo === "Yes"} onChange={() => updateCurrent({ havePo: "Yes" })} />
                  Yes
                </label>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">WTB Type</div>
              <div className="col-span-10 flex items-center gap-4 text-[12px]">
                {(["WTB", "WTS", "WTBD", "WTSQB", "DWIN", "WTS-RENT"] as const).map((v) => (
                  <label key={v} className="flex items-center gap-1">
                    <input type="radio" checked={current.responseType === v} onChange={() => updateCurrent({ responseType: v })} />
                    {v}
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">Status</div>
              {isFindMode ? (
                <Select showSearch allowClear className="col-span-10" value={findFields.dStatus || undefined} options={statusOptions} onChange={(value) => setFindFields((p) => ({ ...p, dStatus: value || "" }))} filterOption={exactMatchFilter} placeholder="Search status..." />
              ) : (
                <Select
                  showSearch
                  allowClear
                  className="col-span-10"
                  value={current.statusText || undefined}
                  options={[
                    ...statusOptions,
                    { value: "__edit_response_status__", label: "✎ Edit Status..." },
                  ]}
                  onChange={(value) => {
                    if (value === "__edit_response_status__") { setIsStatusModalOpen(true); return; }
                    updateCurrent({ statusText: String(value || "") });
                  }}
                  filterOption={exactMatchFilter}
                  placeholder="Select status"
                />
              )}
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">eMail</div>
              {isFindMode ? <div className="col-span-9">{fi("email", `col-span-9 ${fieldCls}`)}</div> : (
                <Input className={`col-span-9 ${fieldCls}`} value={current.email} onChange={(e) => updateCurrent({ email: e.target.value })} />
              )}
              <Button
                className={`col-span-1 !px-0 !min-w-0 ${btnCls}`}
                style={{ ...btnStyle, width: 28 }}
                onClick={() => { if (current.email) window.location.href = `mailto:${current.email}`; }}
              >
                @
              </Button>
            </div>

            <div className="grid grid-cols-12 gap-2 items-center mb-1">
              <div className="col-span-2 text-right text-[12px] font-semibold">eMail Subject</div>
              <Input className={`col-span-10 ${fieldCls}`} value={current.subject} onChange={(e) => updateCurrent({ subject: e.target.value })} />
            </div>

            <div className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-2 text-right text-[12px] font-semibold mt-2">Comment</div>
              <Input.TextArea
                className="col-span-10 rounded text-[12px]"
                rows={6}
                value={isFindMode ? "" : current.comments}
                readOnly={isFindMode}
                onChange={(e) => { if (!isFindMode) updateCurrent({ comments: e.target.value }); }}
                placeholder={isFindMode ? "" : undefined}
              />
            </div>

            <div className="flex items-center justify-end mt-4">
              <Button
                className={btnCls}
                style={btnStyle}
                onClick={handleSave}
                loading={saving}
              >
                Save Changes
              </Button>
            </div>

            <div className="mt-5">
              <div className="text-[24px] text-red-600 font-semibold mb-2">Company:</div>
              {/* <div className="grid grid-cols-12 gap-2 items-center mb-2">
                <div className="col-span-2 text-right text-[12px] font-semibold">Company</div>
                <Input
                  className={`col-span-10 ${fieldCls}`}
                  value={current.companyRe}
                  onChange={(e) => updateCurrent({ companyRe: e.target.value })}
                />
              </div> */}
              <div className="grid grid-cols-12 gap-2 items-center mb-2">
                <div className="col-span-2 text-right text-[12px] font-semibold">WTB RE: Email</div>
                <Input
                  className={`col-span-10 ${fieldCls}`}
                  value={current.wtbReEmail}
                  onChange={(e) => updateCurrent({ wtbReEmail: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-2 text-right text-[12px] font-semibold">WTBD RE: Email</div>
                <Input
                  className={`col-span-10 ${fieldCls}`}
                  value={current.wtbdReEmail}
                  onChange={(e) => updateCurrent({ wtbdReEmail: e.target.value })}
                />
              </div>
            </div>
          </>
          </div>
        )}
      </div>

      {isStatusModalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/20"
            onClick={() => {
              setIsStatusModalOpen(false);
              setEditingStatus({ index: -1, value: "" });
              setNewStatus("");
            }}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
            <div className="relative w-80 max-h-96 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-200 p-3">
                <h3 className="text-[12px] font-semibold text-gray-700">
                  Edit Status Options
                </h3>
                <Button
                  type="text"
                  size="small"
                  className="h-5 w-5 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setIsStatusModalOpen(false);
                    setEditingStatus({ index: -1, value: "" });
                    setNewStatus("");
                  }}
                >
                  x
                </Button>
              </div>

              <div className="max-h-64 overflow-y-auto p-3">
                <div className="mb-3 space-y-2">
                  {statusOptions.map((option, index) => (
                    <div
                      key={option.id ?? `${option.value}-${index}`}
                      className="flex items-center gap-1 group"
                    >
                      {editingStatus.index === index ? (
                        <>
                          <Input
                            value={editingStatus.value}
                            onChange={(e) =>
                              setEditingStatus((prev) => ({
                                ...prev,
                                value: e.target.value,
                              }))
                            }
                            size="small"
                            className="h-6 flex-1 text-[10px]"
                            autoFocus
                            onPressEnter={handleSaveStatus}
                          />
                          <Button
                            type="primary"
                            size="small"
                            className="h-5 w-5 p-0"
                            onClick={handleSaveStatus}
                          >
                            ✓
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-5 w-5 p-0 text-gray-500"
                            onClick={() => setEditingStatus({ index: -1, value: "" })}
                          >
                            x
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-[10px]">{option.label}</span>
                          <div className="flex gap-1">
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 text-gray-400 hover:text-blue-600"
                              onClick={() => handleEditStatus(index)}
                            >
                              <EditOutlined className="text-[12px]" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              className="h-4 w-4 p-0 text-gray-400 hover:text-red-600"
                              onClick={() => handleDeleteStatus(index)}
                              disabled={!option.id}
                            >
                              <DeleteOutlined className="text-[12px]" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 pt-2">
                  <div className="mb-1 text-[10px] font-medium text-gray-500">
                    Add New Status
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      placeholder="Enter new status"
                      value={newStatus}
                      onChange={(e) => setNewStatus(e.target.value)}
                      size="small"
                      className="h-6 flex-1 text-[10px]"
                      onPressEnter={handleAddStatus}
                    />
                    <Button
                      type="primary"
                      size="small"
                      className="h-6 px-2 text-[9px] bg-[#004aad]"
                      onClick={handleAddStatus}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isManufacturerModalOpen && (
        <>
          <div
            className="fixed inset-0 z-40 backdrop-blur-sm bg-white/30"
            onClick={() => {
              setIsManufacturerModalOpen(false);
              setEditingManufacturer(null);
              setNewManufacturerName("");
            }}
          />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-20">
            <div className="relative bg-white rounded-lg shadow-xl border border-gray-300 w-96 max-h-[500px] overflow-hidden">
              <div className="p-3 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                <h3 className="text-[14px] font-semibold text-gray-700">Manage Manufacturers</h3>
                <Button
                  type="text"
                  size="small"
                  className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setIsManufacturerModalOpen(false);
                    setEditingManufacturer(null);
                    setNewManufacturerName("");
                  }}
                >
                  ✕
                </Button>
              </div>

              <div className="p-3 max-h-[300px] overflow-y-auto">
                <div className="space-y-2">
                  <div className="text-[11px] font-medium text-gray-500 mb-2">
                    Existing Manufacturers ({manufacturers.length})
                  </div>
                  {manufacturers.map((manufacturer) => (
                    <div
                      key={manufacturer.id}
                      className="flex items-center gap-1 group bg-gray-50 rounded p-1.5"
                    >
                      {editingManufacturer?.id === manufacturer.id ? (
                        <>
                          <Input
                            value={editingManufacturer.name}
                            onChange={(e) =>
                              setEditingManufacturer({ ...editingManufacturer, name: e.target.value })
                            }
                            size="small"
                            className="h-7 text-[11px] flex-1"
                            autoFocus
                            onPressEnter={handleUpdateManufacturer}
                          />
                          <Button
                            type="primary"
                            size="small"
                            className="h-6 w-6 p-0 flex items-center justify-center bg-green-600 hover:bg-green-700"
                            onClick={handleUpdateManufacturer}
                          >
                            <span className="text-[11px]">✓</span>
                          </Button>
                          <Button
                            type="text"
                            size="small"
                            className="h-6 w-6 p-0 flex items-center justify-center text-gray-500"
                            onClick={() => setEditingManufacturer(null)}
                          >
                            <span className="text-[11px]">✕</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="text-[11px] flex-1">{manufacturer.name}</span>
                          <div className="flex gap-1">
                            <Button
                              type="text"
                              size="small"
                              className="h-5 w-5 p-0 flex items-center justify-center text-gray-400 hover:text-blue-600"
                              onClick={() => setEditingManufacturer({ id: manufacturer.id, name: manufacturer.name })}
                            >
                              <EditOutlined className="text-[12px]" />
                            </Button>
                            <Button
                              type="text"
                              size="small"
                              className="h-5 w-5 p-0 flex items-center justify-center text-gray-400 hover:text-red-600"
                              onClick={() => handleDeleteManufacturer(manufacturer.id)}
                            >
                              <DeleteOutlined className="text-[12px]" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {manufacturers.length === 0 && (
                    <div className="text-center text-gray-400 text-[11px] py-4">
                      No manufacturers added yet
                    </div>
                  )}
                </div>
              </div>

              <div className="px-3 py-2 border-t border-gray-200 bg-white">
                <div className="text-[11px] font-medium text-gray-500 mb-2">Add New Manufacturer</div>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Enter manufacturer name"
                    value={newManufacturerName}
                    onChange={(e) => setNewManufacturerName(e.target.value)}
                    size="small"
                    className="h-7 text-[11px] flex-1"
                    onPressEnter={handleAddManufacturer}
                  />
                  <Button
                    type="default"
                    size="small"
                    className="h-7 px-3 text-[11px] bg-[#C0C0C0] hover:!bg-[#b0b0b0] text-black hover:!text-black border border-[#808080] hover:!border-[#808080]"
                    onClick={handleAddManufacturer}
                  >
                    Add
                  </Button>
                </div>
              </div>

              <div className="p-2 border-t border-gray-200 flex justify-end bg-gray-50">
                <Button
                  size="small"
                  className="h-7 px-3 text-[11px]"
                  onClick={() => {
                    setIsManufacturerModalOpen(false);
                    setEditingManufacturer(null);
                    setNewManufacturerName("");
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuotesResponsesSimple;
